import { Button, Dialog, Input } from '@artosera/ui';
import type { TypeContact, Langue } from '@artosera/shared';
import { useOptionsTypeContact } from '../../api/typesContact.js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreerListe, useModifierListe, type ListeNewsletter } from '../../api/newsletter.js';
import { useShellContext } from '../shell/context.js';
import styles from './NewsletterPage.module.css';
/** Création d'une liste, ou modification quand `liste` est fournie. */
export function ListeDialog({
  open,
  onOpenChange,
  liste,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  liste?: ListeNewsletter | null;
}) {
  const { t } = useTranslation(['regie', 'commun', 'contacts']);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t(liste ? 'regie:newsletter.listes.modifier' : 'regie:newsletter.listes.nouvelle')}
      closeLabel={t('commun:actions.fermer')}
    >
      {open ? <FormListe liste={liste ?? null} fermer={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}
function FormListe({ fermer, liste }: { fermer: () => void; liste: ListeNewsletter | null }) {
  const { t } = useTranslation(['regie', 'commun', 'contacts']);
  const { galerieSlug } = useShellContext();
  const typesContact = useOptionsTypeContact(galerieSlug);
  const creer = useCreerListe(galerieSlug);
  const modifier = useModifierListe(galerieSlug);
  const enregistrement = liste ? modifier : creer;
  const [nom, setNom] = useState(liste?.nom ?? '');
  const [description, setDescription] = useState(liste?.description ?? '');
  const [types, setTypes] = useState<TypeContact[]>(liste?.regle.typesContact ?? []);
  const [langues, setLangues] = useState<Langue[]>(liste?.regle.langues ?? []);
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        const corps = {
          nom,
          description,
          regle: { typesContact: types, etiquettes: liste?.regle.etiquettes ?? [], langues },
        };
        if (liste) modifier.mutate({ id: liste.id, corps }, { onSuccess: fermer });
        else creer.mutate(corps, { onSuccess: fermer });
      }}
    >
      <label>
        {t('regie:newsletter.listes.nom')}
        <Input required maxLength={120} value={nom} onChange={(e) => setNom(e.target.value)} />
      </label>
      <label>
        {t('regie:newsletter.listes.description')}
        <Input
          maxLength={500}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
      <fieldset>
        <legend>{t('regie:newsletter.listes.types')}</legend>
        {typesContact.options.map(({ value: v, label }) => (
          <label className={styles.case} key={v}>
            <input
              type="checkbox"
              checked={types.includes(v)}
              onChange={(e) =>
                setTypes(e.target.checked ? [...types, v] : types.filter((x) => x !== v))
              }
            />
            {label}
          </label>
        ))}
      </fieldset>
      <fieldset>
        <legend>{t('regie:newsletter.listes.langues')}</legend>
        {(['fr', 'en'] as const).map((v) => (
          <label className={styles.case} key={v}>
            <input
              type="checkbox"
              checked={langues.includes(v)}
              onChange={(e) =>
                setLangues(e.target.checked ? [...langues, v] : langues.filter((x) => x !== v))
              }
            />
            {v.toUpperCase()}
          </label>
        ))}
      </fieldset>
      {enregistrement.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
      <Button type="submit" disabled={enregistrement.isPending}>
        {t(liste ? 'regie:newsletter.listes.enregistrer' : 'regie:newsletter.listes.creer')}
      </Button>
    </form>
  );
}
